import { useState, useEffect, useCallback } from 'react'
import { BaseModal2 } from '@components/modals/BaseModal2'
import { Breadcrumb } from '@components/shared/Breadcrumb'
import { Button } from '@components/shared/Button'
import { DatePicker } from '@components/shared/DatePicker'
import { Input } from '@components/shared/Input'
import { MultiSelect } from '@components/shared/MultiSelect'
import { Separator } from '@components/shared/Separator'
import { Table } from '@components/shared/Table'
import { Title } from '@components/shared/Title'
import { emotionTheme } from '@styles/theme'
import { useMediaQuery } from '@mui/material'
import { usePermission } from '@hooks/usePermission'
import { useUser } from '@hooks/useUser'
import { PageIds } from '@hooks/useLayout/types'
//import { PROFILE_GU } from '@pages/Preview/helpers/previewGridValidationActions'
import { FilterIcon } from '@assets/svgs/FilterIcon'
import { CloseIcon } from '@assets/svgs/CloseIcon'
import { TrashSimpleIcon, UsersThree, UserCheck, Baby, Island, FirstAidKit, UserMinus } from '@phosphor-icons/react'

import { SimulationsList } from '@pages/People/components/SimulationsList'
import { NewSimulationModal } from '@pages/People/components/modals/NewSimulationModal'
import { RescissionSimulationModal } from '@pages/People/components/modals/RescissionSimulationModal'
import { EditAdmissionSimulationModal } from '@pages/People/components/modals/EditAdmissionSimulationModal'
import { ReturnModal } from '@components/modals/ReturnModal'
import { CollaboratorDetailsModal } from '@pages/People/components/CollaboratorDetailsModal'
import { usePeople } from '@pages/People/context'

import { convertDateToIsoDate } from '@pages/People/components/modals/RescissionSimulationModal/helpers/dateConverter'
import { parseCollaboratorsGrid, type CollaboratorItemProps } from './helpers/parseCollaboratorsGrid'
import { parseCollaboratorsTotalsRow } from './helpers/parseCollaboratorsTotalsRow'
import colaboradoresCardImage from '@assets/images/COLABORADORES_CARD01.png'
import ativosCardImage from '@assets/images/COLABORADORES_CARD02.png'
import licencaCardImage from '@assets/images/COLABORADORES_CARD03.png'
import feriasCardImage from '@assets/images/COLABORADORES_CARD04.png'
import afastadosCardImage from '@assets/images/COLABORADORES_CARD05.png'
import desligadosCardImage from '@assets/images/COLABORADORES_CARD06.png'

import type { CollaboratorsModalProps } from './types'
import type { SimulationListItemProps, SimulationCollaboratorProps } from '@pages/People/context/types'

import * as S from './styles'

export const CollaboratorsModalComponent = ({ canEdit = true, open, onClose, ...rest }: CollaboratorsModalProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)
  const { filiData } = useUser()
  const { canEdit: canEditPage } = usePermission(PageIds.PEOPLE)
  // const isGUProfile = userPermissionsData?.profile?.name === PROFILE_GU
  const canEditFinancial = canEdit && canEditPage
  const {
    getSimulations,
    getDetailedSummary,
    getPeoplePreviewDetails,
    deleteSimulation,
    getCollaboratorsList,
    getCollaboratorDetails,
    getCollaboratorsIndicators,
    editAdmissionSimulation,
    simulationsData,
    isSimulationsLoading,
    simulationsCanEdit,
    collaboratorsListData,
    isCollaboratorsListLoading,
    collaboratorsIndicatorsData,
    isCollaboratorsIndicatorsLoading
  } = usePeople()

  const [filters, setFilters] = useState<{
    name: string
    matricula: string
    cargo: string[]
    status: string[]
    dataInicio: string
  }>({
    name: '',
    matricula: '',
    cargo: [],
    status: [],
    dataInicio: ''
  })

  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [filtersVersion, setFiltersVersion] = useState(0)
  const [sortBy, setSortBy] = useState<string | null>(null)
  const [sortType, setSortType] = useState<'ASC' | 'DESC' | ''>('')

  const [newSimulationModalOpen, setNewSimulationModalOpen] = useState(false)
  const [rescissionSimulationModalOpen, setRescissionSimulationModalOpen] = useState(false)
  const [editAdmissionSimulationModalOpen, setEditAdmissionSimulationModalOpen] = useState(false)
  const [isEditingAdmissionSimulation, setIsEditingAdmissionSimulation] = useState(false)
  const [deleteSimulationModalOpen, setDeleteSimulationModalOpen] = useState(false)
  const [collaboratorDetailsModalOpen, setCollaboratorDetailsModalOpen] = useState(false)
  const [selectedSimulationToDelete, setSelectedSimulationToDelete] = useState<SimulationListItemProps | null>(null)
  const [selectedSimulationToEdit, setSelectedSimulationToEdit] = useState<SimulationListItemProps | null>(null)
  const [isDeletingSimulation, setIsDeletingSimulation] = useState(false)
  const [selectedCollaboratorForDetails, setSelectedCollaboratorForDetails] = useState<CollaboratorItemProps | null>(null)
  
  const [selectedCollaborator, setSelectedCollaborator] = useState<{
    name: string
    matricula: string
    status: string
    dataInicio: string
    cargo: string
    salario: number
    centroLucro: string
  } | null>(null)

  const handleFilterChange = useCallback((field: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }))
  }, [])

  const handleFilterChangeArray = useCallback((field: 'cargo' | 'status', value: (string | number)[]) => {
    const arr = (value ?? []).map(String)
    setFilters(prev => ({ ...prev, [field]: arr }))
  }, [])

  const handleClearFilters = useCallback(() => {
    setFilters({
      name: '',
      matricula: '',
      cargo: [],
      status: [],
      dataInicio: ''
    })
    setCurrentPage(1)
    setFiltersVersion((v) => v + 1)
  }, [])

  const handleSort = useCallback((sortData: { sortBy?: string | null; sortType?: 'ASC' | 'DESC' | null }) => {
    setSortBy(sortData.sortBy ?? null)
    setSortType(sortData.sortType ?? '')
    setCurrentPage(1)
  }, [])

  const handleApplyFilters = useCallback(() => {
    setCurrentPage(1)
    setFiltersVersion((v) => v + 1)
  }, [])


  // Refetch quando abrir modal, mudar página, itemsPerPage ou quando o usuário aplicar/limpar filtros (filtersVersion).
  // Filtros atuais são lidos do state na hora do fetch; não entram nas deps para evitar refetch a cada digitação.
  useEffect(() => {
    if (!open || !filiData?.filiCode || !filiData?.startPeriod) return

    const params: Parameters<typeof getCollaboratorsList>[0] = {
      filiCode: filiData.filiCode,
      referenceDate: filiData.startPeriod,
      currentPage,
      itemsPerPage
    }
    if (filters.name) params.employeeName = filters.name
    if (filters.matricula) params.employeeRegistration = filters.matricula
    if (filters.cargo?.length) params.positions = filters.cargo
    if (filters.status?.length) params.statuses = filters.status
    if (filters.dataInicio) {
      const startDateIso = convertDateToIsoDate(filters.dataInicio)
      if (startDateIso) params.startDateFrom = `${startDateIso}T00:00:00`
    }
    if (sortBy) {
      params.sortBy = sortBy
      params.sortType = sortType ?? ''
    }

    getCollaboratorsList(params)
    getCollaboratorsIndicators({
      filiCode: filiData.filiCode,
      referenceDate: filiData.startPeriod
    })
    getSimulations(filiData.filiCode, filiData.startPeriod)
  }, [open, filiData?.filiCode, filiData?.startPeriod, currentPage, itemsPerPage, filtersVersion, sortBy, sortType, getCollaboratorsList, getCollaboratorsIndicators, getSimulations])

  const handleSeeMoreCollaborator = useCallback(async (data: CollaboratorItemProps) => {
    if (!filiData?.filiCode || !filiData?.startPeriod) return

    setSelectedCollaboratorForDetails(data)
    setCollaboratorDetailsModalOpen(true)

    
    await getCollaboratorDetails({
      filiCode: filiData.filiCode,
      employeeRegistration: data.registration,
      referenceDate: filiData.startPeriod
    })
  }, [filiData?.filiCode, filiData?.startPeriod, getCollaboratorDetails])

  const handleNewSimulationConfirm = useCallback(async (
    type: 'ADMISSAO' | 'RESCISAO',
    collaborator: SimulationCollaboratorProps | null
  ) => {
    if (!canEditFinancial) return

    if (type === 'RESCISAO' && collaborator) {
      setSelectedCollaborator({
        name: collaborator.employeeName,
        matricula: collaborator.employeeRegistration,
        status: collaborator.status,
        dataInicio: collaborator.startDate,
        cargo: collaborator.position,
        salario: collaborator.salary,
        centroLucro: filiData?.filiName || 'N/A'
      })
      setNewSimulationModalOpen(false)
      setRescissionSimulationModalOpen(true)
    } else if (type === 'ADMISSAO') {
      // Admissão: atualizar lista de simulações e resumo detalhado (busca-resumo-detalhado) na tela de pessoas
      if (filiData?.filiCode && filiData?.startPeriod) {
        await getSimulations(filiData.filiCode, filiData.startPeriod)
        // Pequeno delay para o backend refletir a admissão antes de buscar o resumo
        await new Promise((resolve) => setTimeout(resolve, 400))
        await getDetailedSummary({
          filiCode: filiData.filiCode,
          competenceDate: filiData.startPeriod
        })
        await getPeoplePreviewDetails({
          filiCode: filiData.filiCode,
          competenceDate: filiData.startPeriod
        })
      }
    }
  }, [canEditFinancial, filiData?.filiCode, filiData?.startPeriod, filiData?.filiName, getSimulations, getDetailedSummary, getPeoplePreviewDetails])

  const handleRescissionSimulationConfirm = (data: {
    tipoAviso: 'TRABALHADO' | 'INDENIZADO'
    dataRescisao: string
    semJustaCausa: boolean
  }) => {
    console.log('Dados da simulação de rescisão:', data)
    setRescissionSimulationModalOpen(false)
    setSelectedCollaborator(null)
    
  
    if (filiData?.filiCode && filiData?.startPeriod) {
      getSimulations(filiData.filiCode, filiData.startPeriod)
    }
  }

  const handleEditSimulation = (simulation: SimulationListItemProps) => {
    if (!canEditFinancial) return

    const normalizedType = simulation.simulationType?.toLowerCase()
    
    if (normalizedType === 'rescisao' || normalizedType === 'rescisão') {
      setSelectedSimulationToEdit(simulation)
      setRescissionSimulationModalOpen(true)
    } else if (normalizedType === 'admissao' || normalizedType === 'admissão') {
      setSelectedSimulationToEdit(simulation)
      setEditAdmissionSimulationModalOpen(true)
    }
  }

  const handleEditAdmissionSimulationConfirm = async (data: {
    position: string
    startDate: string
    salary: number
  }) => {
    if (!canEditFinancial) return
    if (!selectedSimulationToEdit || !filiData?.filiCode || !filiData?.startPeriod) return

    setIsEditingAdmissionSimulation(true)

    try {
      await editAdmissionSimulation(filiData.filiCode, selectedSimulationToEdit.id, data, filiData.startPeriod)
      setEditAdmissionSimulationModalOpen(false)
      setSelectedSimulationToEdit(null)
      // Atualizar resumo detalhado na tela de pessoas (o provider já chama getDetailedSummary; refetch aqui garante a UI)
      await getDetailedSummary({
        filiCode: filiData.filiCode,
        competenceDate: filiData.startPeriod
      })
      await getPeoplePreviewDetails({
        filiCode: filiData.filiCode,
        competenceDate: filiData.startPeriod
      })
    } catch {
      // erro já tratado no provider
    } finally {
      setIsEditingAdmissionSimulation(false)
    }
  }

  const handleDeleteSimulation = (simulation: SimulationListItemProps) => {
    if (!canEditFinancial) return

    setSelectedSimulationToDelete(simulation)
    setDeleteSimulationModalOpen(true)
  }

  const handleConfirmDeleteSimulation = async () => {
    if (!canEditFinancial) return
    if (!selectedSimulationToDelete || !filiData?.filiCode || !filiData?.startPeriod) return

    setIsDeletingSimulation(true)
    const success = await deleteSimulation(
      selectedSimulationToDelete.id,
      filiData.filiCode,
      filiData.startPeriod
    )

    if (success) {
      setDeleteSimulationModalOpen(false)
      setSelectedSimulationToDelete(null)
    }

    setIsDeletingSimulation(false)
  }

  
  const collaboratorsData: CollaboratorItemProps[] = (collaboratorsListData?.items || []).map(item => ({
    name: item.employeeName,
    registration: item.employeeRegistration,
    position: item.position,
    startDate: item.startDate,
    status: item.status as any,
    profitCenter: filiData?.filiCode,
    salaries: item.salaries ?? 0,
    benefits: item.benefits ?? 0,
    charges: item.charges ?? 0,
    grandTotal: item.grandTotal ?? 0
  }))

  
  const cargoOptions = (collaboratorsListData?.availableFilters?.positions || []).map((position) => ({
    value: position,
    label: position
  }))

  const statusOptions = (collaboratorsListData?.availableFilters?.statuses || []).map((status) => ({
    value: status,
    label: status
  }))

  const collaboratorsGrid = parseCollaboratorsGrid({
    data: collaboratorsData,
    onSeeMore: handleSeeMoreCollaborator
  })

  return (
    <BaseModal2
      open={open}
      width={isMobile ? 'calc(100% - 20px)' : '96.5%'}
      heightContainer={isMobile ? 'calc(100% - 20px)' : '95%'}
      headerIcon={
        <Breadcrumb
          breadcrumbData={[
            { label: 'Pessoal' },
            { label: `${filiData?.filiCode} - ${filiData?.filiName} ` },
            { label: 'Gestão de Colaboradores' }
          ]}
        />
      }
      onClose={onClose}
      {...rest}
    >
      <S.CollaboratorsModal>
        <Title label="Gestão de Colaboradores" />

        <S.CardsGrid>
          <S.CollaboratorsCard borderColor="rgba(30, 64, 175, 0.24)">
            <S.CardContent>
              <S.IconContainer>
                <UsersThree size={72} weight="regular" color="#1E40AF" />
              </S.IconContainer>
              <S.TotalNumber color="#1E40AF">
                {isCollaboratorsIndicatorsLoading ? '...' : (collaboratorsIndicatorsData?.employees?.toString().padStart(4, '0') || '0000')}
              </S.TotalNumber>
            </S.CardContent>
            <S.CardLabel src={colaboradoresCardImage} alt="Colaboradores" />
          </S.CollaboratorsCard>

          <S.CollaboratorsCard borderColor="rgba(22, 163, 74, 0.24)">
            <S.CardContent>
              <S.IconContainer>
                <UserCheck size={72} weight="regular" color="#16A34A" />
              </S.IconContainer>
              <S.TotalNumber color="#16A34A">
                {isCollaboratorsIndicatorsLoading ? '...' : (collaboratorsIndicatorsData?.active?.toString().padStart(4, '0') || '0000')}
              </S.TotalNumber>
            </S.CardContent>
            <S.CardLabel src={ativosCardImage} alt="Ativos" />
          </S.CollaboratorsCard>

          <S.CollaboratorsCard borderColor="rgba(236, 72, 153, 0.24)">
            <S.CardContent>
              <S.IconContainer>
                <Baby size={72} weight="regular" color="#EC4899" />
              </S.IconContainer>
              <S.TotalNumber color="#EC4899">
                {isCollaboratorsIndicatorsLoading ? '...' : (collaboratorsIndicatorsData?.onLeave?.toString().padStart(4, '0') || '0000')}
              </S.TotalNumber>
            </S.CardContent>
            <S.CardLabel src={licencaCardImage} alt="Licença" />
          </S.CollaboratorsCard>

          <S.CollaboratorsCard borderColor="rgba(234, 179, 8, 0.24)">
            <S.CardContent>
              <S.IconContainer>
                <Island size={72} weight="regular" color="#EAB308" />
              </S.IconContainer>
              <S.TotalNumber color="#EAB308">
                {isCollaboratorsIndicatorsLoading ? '...' : (collaboratorsIndicatorsData?.onVacation?.toString().padStart(4, '0') || '0000')}
              </S.TotalNumber>
            </S.CardContent>
            <S.CardLabel src={feriasCardImage} alt="Férias" />
          </S.CollaboratorsCard>

          <S.CollaboratorsCard borderColor="rgba(220, 38, 38, 0.24)">
            <S.CardContent>
              <S.IconContainer>
                <FirstAidKit size={72} weight="regular" color="#DC2626" />
              </S.IconContainer>
              <S.TotalNumber color="#DC2626">
                {isCollaboratorsIndicatorsLoading ? '...' : (collaboratorsIndicatorsData?.onMedicalLeave?.toString().padStart(4, '0') || '0000')}
              </S.TotalNumber>
            </S.CardContent>
            <S.CardLabel src={afastadosCardImage} alt="Afastados" />
          </S.CollaboratorsCard>

          <S.CollaboratorsCard borderColor="rgba(107, 114, 128, 0.24)">
            <S.CardContent>
              <S.IconContainer>
                <UserMinus size={72} weight="regular" color="#6B7280" />
              </S.IconContainer>
              <S.TotalNumber color="#6B7280">
                {isCollaboratorsIndicatorsLoading ? '...' : (collaboratorsIndicatorsData?.terminated?.toString().padStart(4, '0') || '0000')}
              </S.TotalNumber>
            </S.CardContent>
            <S.CardLabel src={desligadosCardImage} alt="Desligados" />
          </S.CollaboratorsCard>
        </S.CardsGrid>

        <Separator height={24} />
        
        <Title label="Lista de Colaboradores" />
        
        <S.FiltersContainer>
          <S.FilterRow>
            <Input
              name="name"
              label="Nome do Colaborador"
              placeholder="Insira o nome do colaborador"
              value={filters.name}
              onChange={(e) => handleFilterChange('name', e.target.value)}
              hasController={false}
            />
          </S.FilterRow>

          <S.FilterRow>
            <Input
              name="matricula"
              label="Matrícula"
              placeholder="Insira a Matrícula do colaborador"
              value={filters.matricula}
              onChange={(e) => handleFilterChange('matricula', e.target.value)}
              hasController={false}
            />
          </S.FilterRow>

          <S.ButtonsContainer>
            <Button
              width="100%"
              colorType="outlineBlue"
              rightIcon={<FilterIcon />}
              onClick={handleApplyFilters}
            >
              Filtrar
            </Button>
            <Button
              width="100%"
              colorType="outlineBlue"
              rightIcon={<CloseIcon />}
              onClick={handleClearFilters}
            >
              Limpar
            </Button>
          </S.ButtonsContainer>

          <S.FilterRow>
            <MultiSelect
              name="cargo"
              label="Cargo"
              placeholder="Selecione o(s) cargo(s)"
              options={cargoOptions}
              value={filters.cargo}
              onChange={(value) => handleFilterChangeArray('cargo', Array.isArray(value) ? value : [])}
              hasController={false}
            />
          </S.FilterRow>

          <S.FilterRow>
            <MultiSelect
              name="status"
              label="Status"
              placeholder="Selecione o(s) status"
              options={statusOptions}
              value={filters.status}
              onChange={(value) => handleFilterChangeArray('status', Array.isArray(value) ? value : [])}
              hasController={false}
            />
          </S.FilterRow>

          <S.FilterRow>
            <DatePicker
              name="dataInicio"
              label="Data Início"
              placeholder="Selecione a data"
              value={filters.dataInicio}
              onChange={(date) => handleFilterChange('dataInicio', date || '')}
              hasController={false}
            />
          </S.FilterRow>
        </S.FiltersContainer>

        <Table
          data={collaboratorsGrid}
          totalData={parseCollaboratorsTotalsRow(collaboratorsGrid[0] ?? [], collaboratorsListData?.totals)}
          onSort={handleSort}
          footer={{
            pagination: {
              currentPage: collaboratorsListData?.currentPage ?? currentPage,
              itemsPerPage: collaboratorsListData?.itemsPerPage ?? itemsPerPage,
              totalItems: collaboratorsListData?.totalItems ?? 0,
              totalPages: collaboratorsListData?.totalPages ?? 1,
              onPageChange: (page) => setCurrentPage(page),
              onItemsPerPageChange: (items) => {
                setItemsPerPage(items)
                setCurrentPage(1)
              }
            }
          }}
          isLoading={isCollaboratorsListLoading}
        />

        <Separator height={32} />

        <SimulationsList
          simulations={simulationsData || []}
          isLoading={isSimulationsLoading}
          onEdit={handleEditSimulation}
          onDelete={handleDeleteSimulation}
          onNewSimulation={canEditFinancial ? () => setNewSimulationModalOpen(true) : undefined}
          canEdit={canEditFinancial}
          canEditSimulations={simulationsCanEdit}
        />
 
      </S.CollaboratorsModal>

      <NewSimulationModal
        open={newSimulationModalOpen}
        onClose={setNewSimulationModalOpen}
        onConfirm={handleNewSimulationConfirm}
        isLoading={false}
        collaboratorFilters={filters}
        itemsPerPage={itemsPerPage}
        canEdit={canEditFinancial}
      />

      <RescissionSimulationModal
        open={rescissionSimulationModalOpen}
        onClose={(value) => {
          setRescissionSimulationModalOpen(value)
          if (!value) {
            setSelectedCollaborator(null)
            setSelectedSimulationToEdit(null)
          }
        }}
        collaborator={selectedCollaborator}
        onConfirm={handleRescissionSimulationConfirm}
        isLoading={false}
        simulationToEdit={selectedSimulationToEdit}
        isEditMode={!!selectedSimulationToEdit}
        canEdit={canEditFinancial}
      />

      <EditAdmissionSimulationModal
        open={editAdmissionSimulationModalOpen}
        onClose={(value) => {
          setEditAdmissionSimulationModalOpen(value)
          if (!value) {
            setSelectedSimulationToEdit(null)
          }
        }}
        simulation={selectedSimulationToEdit}
        onConfirm={handleEditAdmissionSimulationConfirm}
        isLoading={isEditingAdmissionSimulation}
        canEdit={canEditFinancial}
      />

      <ReturnModal
        open={deleteSimulationModalOpen}
        headerIcon={<TrashSimpleIcon size={24} weight="bold" />}
        headerTitle="Excluir Simulação"
        title="Tem certeza de que deseja excluir esta simulação?"
        message="Esta ação não poderá ser desfeita."
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={() => {
          setDeleteSimulationModalOpen(false)
          setSelectedSimulationToDelete(null)
        }}
        onConfirm={handleConfirmDeleteSimulation}
        isLoading={isDeletingSimulation}
      />

      <CollaboratorDetailsModal
        open={collaboratorDetailsModalOpen}
        onClose={(value) => {
          setCollaboratorDetailsModalOpen(value)
          if (!value) {
            setSelectedCollaboratorForDetails(null)
          }
        }}
        collaborator={selectedCollaboratorForDetails}
        canEdit={canEditFinancial}
      />
    </BaseModal2>
  )
}
